import { Component, ChangeDetectionStrategy, inject, DestroyRef, viewChildren, effect, signal, PendingTasks } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { firstValueFrom } from 'rxjs';

import { DynamicSectionDirective } from '../../directives/dynamic-section.directive';
import { PageService } from '../../providers/page.service';
import { EditorId, EditorIdService } from '../../providers/editor-id.service';
import { AnySection } from '../../types/section';
import { isPreviewPage, Page } from '../../types/page';
import { CurrentPageSearchService } from '../../../app/providers/current-page-search.service';
import { CommonModule } from '@angular/common';
import { ComponentFactoryService } from '@knorr-bremse-portals/ngx-components';
import { outputToObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MainGridComponent } from '@knorr-bremse-portals/ngx-components/components/main-grid';
import { ProgressIndicatorComponent } from '@knorr-bremse-portals/ngx-components/components/progress-indicator';
import { EditorIdDirective } from '../../directives/editor-id.directive';

@Component({
    styleUrls: ['./dynamic-page.component.scss'],
    templateUrl: './dynamic-page.component.html',
    providers: [EditorIdService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        DynamicSectionDirective,
        MainGridComponent,
        ProgressIndicatorComponent,
        EditorIdDirective,
    ],
})
export class DynamicPageComponent {

  protected sections = signal<AnySection[]>([]);
  protected editorId?: EditorId;

  private componentFactoryService = inject(ComponentFactoryService);

  private dynamicSections = viewChildren(DynamicSectionDirective);

  private pageService = inject(PageService);
  private route = inject(ActivatedRoute);
  private currentPageSearchService = inject(CurrentPageSearchService);
  private destroyRef = inject(DestroyRef);

  protected loading$ = this.pageService.loading$;

  private pendingTasks = inject(PendingTasks);

  public constructor() {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      const currentPage = data['page'] as Page;

      if (isPreviewPage(currentPage)) {
        this.editorId = currentPage.editorId;
      }

      this.pageService.updatePage(currentPage);
      this.currentPageSearchService.clear();
      this.render(currentPage);
    });

    const firstRenderTask = this.pendingTasks.add();
    effect(async () => {
      const dynamicSections = this.dynamicSections();
      if (dynamicSections.length === 0) {
        return;
      }

      const sectionRenderedPromises = [];
      for (const dynamicSection of dynamicSections) {
        const afterRender$ = outputToObservable(dynamicSection.afterRender);
        sectionRenderedPromises.push(firstValueFrom(afterRender$));
      }
      await Promise.all(sectionRenderedPromises);

      this.pageService.dynamicSectionsRendered.next(); // Notify any subscribing service, that the page has fully loaded

      firstRenderTask();
    });
  }

  private async render(page: Page) {
    const componentMirrorPromises = [];
    for (const section of page.sections) {
      componentMirrorPromises.push(this.componentFactoryService.getComponentMirrorByName(section.component));
    }
    await Promise.all(componentMirrorPromises); // pre fetch all lazy loaded components at the same time before clearing the screen

    this.sections.set(page.sections);
  }

}
